/**
 * Limpieza de estilos residuales al pegar desde Google Docs (y Word).
 *
 * El problema que resuelve: Google Docs anida un `<span style="font-size:11pt">`
 * DENTRO del span al que CKEditor aplica el tamaño elegido. Ambos son estilos
 * inline, así que el interno gana por proximidad y el tamaño del selector no se
 * ve. El síntoma es característico: la línea crece en vertical (el `line-height`
 * sí toma el valor del span externo) pero el glifo se dibuja siempre al mismo
 * tamaño.
 *
 * Se limpia el HTML del portapapeles ANTES de que CKEditor lo convierta a su
 * modelo, que es donde el marcado todavía es HTML plano y manipularlo no tiene
 * efectos secundarios sobre el esquema del editor.
 *
 * Solo actúa sobre contenido pegado: lo que se escribe en el editor no pasa por
 * aquí, y el contenido ya guardado en base de datos no se toca.
 */

/**
 * Propiedades que se eliminan de los `style` inline del contenido pegado.
 *
 * `font-size` y `font-family` se retiran para que el texto adopte el tamaño y la
 * tipografía del bloque en lugar de imponer los de Google Docs. El resto son
 * declaraciones inertes que Google Docs escribe en cada span y que solo engordan
 * el marcado.
 *
 * Se conservan a propósito `color`, `font-weight` y `background-color`: son
 * formato real del autor (negritas, resaltados, color de texto).
 */
const PROPIEDADES_A_LIMPIAR = [
  'font-size',
  'font-family',
  'white-space',
  'vertical-align',
  'font-variant',
  'text-decoration',
];

/**
 * `text-decoration` solo se elimina cuando vale `none`: con cualquier otro valor
 * (`underline`, `line-through`) es formato deliberado del autor.
 */
const SOLO_SI_ES_NINGUNO = new Set(['text-decoration']);

/**
 * `font-size` se elimina solo cuando viene en **puntos**.
 *
 * Es lo que distingue el residuo del formato real: Google Docs escribe siempre
 * `pt` (11pt, 13pt, 17pt…) y el plugin `fontSize` de CKEditor escribe `px`,
 * porque este proyecto lo configura con opciones numéricas. Sin esta distinción
 * la limpieza borraría también el tamaño que el usuario acaba de elegir, y
 * pegar contenido con tamaños ya aplicados los perdería todos.
 */
const UNIDAD_DE_PROCESADOR_DE_TEXTO = /\d\s*pt\s*$/i;

/**
 * Quita de una cadena `style` las propiedades listadas, conservando el resto.
 * Devuelve la cadena resultante ya normalizada, o cadena vacía si no queda nada.
 */
export function limpiarEstilosResiduales(style) {
  if (!style) return '';

  return style
    .split(';')
    .map((declaracion) => declaracion.trim())
    .filter(Boolean)
    .filter((declaracion) => {
      const separador = declaracion.indexOf(':');
      if (separador === -1) return true;

      const propiedad = declaracion.slice(0, separador).trim().toLowerCase();
      const valor = declaracion.slice(separador + 1).trim().toLowerCase();

      if (!PROPIEDADES_A_LIMPIAR.includes(propiedad)) return true;
      if (SOLO_SI_ES_NINGUNO.has(propiedad)) return valor !== 'none';
      if (propiedad === 'font-size') return !UNIDAD_DE_PROCESADOR_DE_TEXTO.test(valor);

      return false;
    })
    .join('; ');
}

/**
 * Limpia el HTML del portapapeles como texto, antes de que CKEditor lo convierta
 * en un fragmento de vista.
 *
 * Se trabaja sobre la cadena y no sobre el árbol de la vista a propósito: el
 * `DocumentFragment` que viaja en `inputTransformation` necesita un
 * `UpcastWriter` para modificarse —`editor.editing.view.change()` gobierna el
 * árbol de edición, no ese fragmento, así que escribir ahí no surte efecto— y
 * este build (`ckeditor5-resize-nimbus-build` 0.0.3) no exporta `UpcastWriter`.
 * En `clipboardInput` el contenido es todavía HTML plano, donde un reemplazo de
 * cadena es suficiente y no depende de la API interna del editor.
 */
export function limpiarHtmlPegado(html) {
  if (!html) return '';

  return (
    html
      // El `<meta charset>` que Google Docs antepone al portapapeles: CKEditor
      // no sabe colocarlo y acaba como <span data-ck-unsafe-element="meta">
      // dentro de un párrafo vacío.
      .replace(/<meta[^>]*>/gi, '')
      // `style` con comillas dobles o simples.
      .replace(/\sstyle=(["'])(.*?)\1/gi, (completo, comilla, valor) => {
        const limpio = limpiarEstilosResiduales(valor);
        return limpio ? ` style=${comilla}${limpio}${comilla}` : '';
      })
  );
}

/**
 * Plugin de CKEditor. Debe incluirse en `extraPlugins` de los editores que
 * acepten contenido pegado desde procesadores de texto.
 */
export default function NimbusPasteStyleCleanupPlugin(editor) {
  editor.plugins.get('ClipboardPipeline').on(
    'inputTransformation',
    (evt, data) => {
      if (!data.content) return;

      // Se serializa el fragmento ya construido, se limpia como texto y se
      // vuelve a parsear. Evita depender de `UpcastWriter` —no exportado en
      // este build— y, sobre todo, actúa DESPUÉS de Paste from Office, que
      // reconstruye el contenido a partir del `dataTransfer` original y
      // descartaba cualquier cambio hecho antes (ver nota abajo).
      const processor = editor.data.processor;
      const html = processor.toData(data.content);
      const limpio = limpiarHtmlPegado(html);

      if (limpio !== html) {
        data.content = processor.toView(limpio);
      }
    },
    // Prioridad baja: Paste from Office se engancha a `clipboardInput` con
    // prioridad alta y reconstruye el contenido leyendo el `dataTransfer`
    // original. Limpiar antes que él —o reescribir el `dataTransfer`, que
    // además es de solo lectura durante el evento `paste`— no tiene efecto:
    // su resultado sobrescribe el nuestro. Hay que ser los últimos.
    { priority: 'low' }
  );
}
